import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import api, { removeAuthToken } from "@/lib/api";
import { logoutAdmin, logoutStaff } from "@/lib/store";
import {
  LogOut,
  RefreshCw,
  CheckCircle,
  XCircle,
  Truck,
  PlusCircle,
  Gift,
  Trash2,
  UtensilsCrossed,
  Compass,
  Shield,
  Search,
  ExternalLink,
  Leaf,
  Plus,
  Calendar,
  Layers,
  Bell,
  Check,
  Building2,
  Clock,
  DollarSign
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const MEAL_PLANS = ["EP", "CP", "MAP", "Complimentary", "Staff Meal", "Other"];
const PAYMENT_MODES = ["Cash", "UPI", "Pay at Front Desk / Room Bill"];

const CATEGORIES_LIST = [
  { key: "breakfast", label: "Breakfast", icon: "🌅" },
  { key: "lunch", label: "Lunch", icon: "☀️" },
  { key: "snacks", label: "Snacks", icon: "🍿" },
  { key: "beverages", label: "Beverages", icon: "☕" },
  { key: "dinner", label: "Dinner", icon: "🌙" },
  { key: "desserts", label: "Desserts", icon: "🍰" },
  { key: "todays_special", label: "Today's Special", icon: "⭐" },
  { key: "combo", label: "Combo", icon: "🍱" },
];

export default function StaffDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const staffUser = localStorage.getItem("hm_staff_user");
  const parsedUser = staffUser ? JSON.parse(staffUser) : null;
  const propertyId = searchParams.get("property") || (parsedUser ? parsedUser.property_id : "");
  const initialTab = searchParams.get("tab") || "orders";

  const [user, setUser] = useState(null);
  const [properties, setProperties] = useState([]);
  const [orders, setOrders] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [requests, setRequests] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [propertySettings, setPropertySettings] = useState(null);
  const [categorySettings, setCategorySettings] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [activeTab, setActiveTab] = useState(initialTab);

  // Filters & Search
  const [orderFilter, setOrderFilter] = useState("all");
  const [bookingFilter, setBookingFilter] = useState("all");
  const [requestFilter, setRequestFilter] = useState("all");
  const [orderSearch, setOrderSearch] = useState("");
  const [bookingSearch, setBookingSearch] = useState("");
  const [menuSearch, setMenuSearch] = useState("");
  const [menuFilterCat, setMenuFilterCat] = useState("all");
  const [dateRange, setDateRange] = useState("today"); // "today" | "7days"

  // Helper: filter items by date range
  const applyDateRange = (items) => {
    const now = new Date();
    const todayStr = now.toISOString().split("T")[0];
    if (dateRange === "today") {
      return items.filter((item) => {
        const d = item.created_at ? new Date(item.created_at).toISOString().split("T")[0] : "";
        return d === todayStr;
      });
    }
    // 7 days
    const cutoff = new Date(now);
    cutoff.setDate(cutoff.getDate() - 7);
    return items.filter((item) => item.created_at && new Date(item.created_at) >= cutoff);
  };

  // Toggling states
  const [togglingFood, setTogglingFood] = useState(false);
  const [togglingExp, setTogglingExp] = useState(false);
  const [togglingCat, setTogglingCat] = useState({});
  const [togglingItem, setTogglingItem] = useState({});

  // Manual Food Order Modal state
  const [showManualOrderDialog, setShowManualOrderDialog] = useState(false);
  const [manualOrderSaving, setManualOrderSaving] = useState(false);
  const [manualOrderForm, setManualOrderForm] = useState({
    guest_name: "",
    room_number: "",
    whatsapp: "",
    payment_mode: "Cash",
    notes: "",
    items: [{ item_id: "", name: "", quantity: 1, price: 0 }],
  });

  // Manual Experience Booking Modal state
  const [showManualBookingDialog, setShowManualBookingDialog] = useState(false);
  const [manualBookingSaving, setManualBookingSaving] = useState(false);
  const [manualBookingForm, setManualBookingForm] = useState({
    experience_id: "",
    experience_title: "",
    guest_name: "",
    room_number: "",
    whatsapp: "",
    booking_date: new Date().toISOString().split("T")[0],
    date_slots: "Morning 6:00 AM",
    guests: 1,
    payment_mode: "Cash",
    notes: "",
  });

  // Complimentary order state
  const [showComplimentaryDialog, setShowComplimentaryDialog] = useState(false);
  const [compSaving, setCompSaving] = useState(false);
  const [compForm, setCompForm] = useState({
    guest_name: "",
    room_number: "",
    meal_plan: "CP",
    booking_reference: "",
    notes: "",
    items: [{ item_id: "", name: "", quantity: 1 }],
  });

  const impersonator = localStorage.getItem("hm_impersonator");

  useEffect(() => {
    const staffUser = localStorage.getItem("hm_user");
    if (!staffUser) {
      navigate(`/team/login?property=${propertyId}`);
      return;
    }
    const parsedUser = JSON.parse(staffUser);
    setUser(parsedUser);
    fetchProperties();
    fetchData();
    fetchMenuItems();
    fetchExperiences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  const fetchProperties = async () => {
    try {
      const res = await api.get("/settings");
      if (Array.isArray(res.data) && res.data.length > 0) {
        setProperties(res.data);
      }
    } catch (e) {
      // Non-critical
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [ordersRes, bookingsRes, requestsRes, settingsRes, catSettingsRes] = await Promise.all([
        api.get(`/orders?property_id=${propertyId}`),
        api.get(`/bookings?property_id=${propertyId}`),
        api.get(`/requests?property_id=${propertyId}`),
        api.get(`/settings/${propertyId}`).catch(() => ({ data: null })),
        api.get(`/category-settings/${propertyId}`).catch(() => ({ data: [] })),
      ]);
      setOrders(Array.isArray(ordersRes.data) ? ordersRes.data : []);
      setBookings(Array.isArray(bookingsRes.data) ? bookingsRes.data : []);
      setRequests(Array.isArray(requestsRes.data) ? requestsRes.data : []);
      if (settingsRes?.data) {
        setPropertySettings(settingsRes.data);
      }
      if (Array.isArray(catSettingsRes?.data)) {
        setCategorySettings(catSettingsRes.data);
      }
    } catch (error) {
      setError(error?.response?.data?.detail || "Network error. Failed to load data.");
      toast.error("Failed to load operations data");
    } finally {
      setLoading(false);
    }
  };

  const fetchMenuItems = async () => {
    try {
      const res = await api.get(`/menu?property_id=${propertyId}&available_only=false`);
      setMenuItems(res.data || []);
    } catch (e) {
      // Non-critical
    }
  };

  const fetchExperiences = async () => {
    try {
      const res = await api.get(`/experiences?property_id=${propertyId}&active_only=false`);
      setExperiences(res.data || []);
    } catch (e) {
      // Non-critical
    }
  };

  const handlePropertyChange = (newProp) => {
    setSearchParams({ property: newProp, tab: activeTab });
  };

  const handleToggleFood = async () => {
    setTogglingFood(true);
    try {
      const res = await api.post(`/settings/${propertyId}/toggle-food`);
      const newVal = res.data.food_enabled;
      setPropertySettings((prev) => ({ ...(prev || {}), food_enabled: newVal }));
      setCategorySettings((prev) => prev.map((c) => ({ ...c, is_enabled: newVal, is_active: newVal })));
      toast.success(newVal ? "Restaurant is now OPEN 🟢" : "Restaurant is now CLOSED 🔴");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to toggle restaurant");
    } finally {
      setTogglingFood(false);
    }
  };

  const handleToggleExperiences = async () => {
    setTogglingExp(true);
    try {
      const res = await api.post(`/settings/${propertyId}/toggle-experiences`);
      const newVal = res.data.experiences_enabled;
      setPropertySettings((prev) => ({ ...(prev || {}), experiences_enabled: newVal }));
      toast.success(newVal ? "Experiences booking is now ACTIVE 🟢" : "Experiences booking is now PAUSED 🔴");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to toggle experiences");
    } finally {
      setTogglingExp(false);
    }
  };

  const handleToggleCategory = async (category) => {
    setTogglingCat((prev) => ({ ...prev, [category]: true }));
    try {
      const res = await api.post(`/category-settings/${propertyId}/${category}/toggle`);
      const isEnabled = res.data.is_enabled;
      setCategorySettings((prev) => {
        const exists = prev.some((c) => c.category === category);
        if (exists) {
          return prev.map((c) => (c.category === category ? { ...c, is_enabled: isEnabled, is_active: isEnabled } : c));
        } else {
          return [...prev, { category, is_enabled: isEnabled, is_active: isEnabled, property_id: propertyId }];
        }
      });
      toast.success(`${category.charAt(0).toUpperCase() + category.slice(1)} is now ${isEnabled ? "OPEN 🟢" : "CLOSED 🔴"}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to toggle ${category}`);
    } finally {
      setTogglingCat((prev) => ({ ...prev, [category]: false }));
    }
  };

  const handleToggleItemAvailability = async (itemId) => {
    setTogglingItem((prev) => ({ ...prev, [itemId]: true }));
    try {
      const res = await api.post(`/menu/${itemId}/toggle-availability`);
      const newAvail = res.data.is_available;
      setMenuItems((prev) => prev.map((item) => (item.id === itemId ? { ...item, is_available: newAvail } : item)));
      toast.success(`Dish marked as ${newAvail ? "Available 🟢" : "Out of Stock 🔴"}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update item availability");
    } finally {
      setTogglingItem((prev) => ({ ...prev, [itemId]: false }));
    }
  };

  const handleLogout = () => {
    logoutAdmin();
    logoutStaff();
    removeAuthToken();
    localStorage.removeItem("hm_user");
    localStorage.removeItem("hm_impersonator");
    navigate(`/team/login?property=${propertyId}`);
  };

  const handleReturnToAdmin = () => {
    localStorage.removeItem("hm_impersonator");
    localStorage.setItem("hm_admin_auth", "true");
    navigate("/admin/users");
  };

  const updateStatus = async (type, id, status) => {
    setActionLoading(id);
    try {
      await api.put(`/${type}/${id}/status?status=${status}`);
      toast.success(`Status updated to ${status}`);
      await silentRefetch();
    } catch (error) {
      toast.error("Failed to update status");
    } finally {
      setActionLoading(null);
    }
  };

  // Refresh data without triggering the full-page spinner (used after status updates)
  const silentRefetch = async () => {
    try {
      const [ordersRes, bookingsRes, requestsRes] = await Promise.all([
        api.get(`/orders?property_id=${propertyId}`),
        api.get(`/bookings?property_id=${propertyId}`),
        api.get(`/requests?property_id=${propertyId}`),
      ]);
      setOrders(Array.isArray(ordersRes.data) ? ordersRes.data : []);
      setBookings(Array.isArray(bookingsRes.data) ? bookingsRes.data : []);
      setRequests(Array.isArray(requestsRes.data) ? requestsRes.data : []);
    } catch {
      // Silently ignore — full fetchData will catch it on next poll
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-800 border-yellow-300",
      approved: "bg-blue-100 text-blue-800 border-blue-300",
      paid: "bg-emerald-100 text-emerald-800 border-emerald-300",
      confirmed: "bg-emerald-100 text-emerald-800 border-emerald-300",
      delivered: "bg-teal-100 text-teal-800 border-teal-300",
      rejected: "bg-red-100 text-red-800 border-red-300",
      cancelled: "bg-red-100 text-red-800 border-red-300",
      complimentary: "bg-purple-100 text-purple-800 border-purple-300",
      vendor_unresponsive: "bg-orange-100 text-orange-800 border-orange-300",
      in_progress: "bg-sky-100 text-sky-800 border-sky-300",
      resolved: "bg-green-100 text-green-800 border-green-300",
      completed: "bg-green-100 text-green-800 border-green-300",
    };
    return (
      <Badge className={`${colors[status] || "bg-gray-100 text-gray-700"} capitalize border font-semibold text-xs`}>
        {status}
      </Badge>
    );
  };

  // ---- Manual Food Order Helpers ----
  const addManualOrderItem = () => {
    setManualOrderForm((f) => ({
      ...f,
      items: [...f.items, { item_id: "", name: "", quantity: 1, price: 0 }],
    }));
  };

  const removeManualOrderItem = (idx) => {
    setManualOrderForm((f) => ({
      ...f,
      items: f.items.filter((_, i) => i !== idx),
    }));
  };

  const updateManualOrderItem = (idx, field, value) => {
    setManualOrderForm((f) => {
      const items = [...f.items];
      items[idx] = { ...items[idx], [field]: value };
      if (field === "item_id") {
        const found = menuItems.find((m) => m.id === value);
        if (found) {
          items[idx].name = found.name;
          items[idx].price = Number(found.price) || 0;
        }
      }
      return { ...f, items };
    });
  };

  const calculateManualOrderTotal = () => {
    return (manualOrderForm.items || []).reduce((sum, it) => {
      const p = Number(it.price) || 0;
      const q = Number(it.quantity) || 1;
      return sum + p * q;
    }, 0);
  };

  const handleSubmitManualOrder = async () => {
    if (!manualOrderForm.guest_name.trim() || !manualOrderForm.room_number.trim()) {
      toast.error("Guest name and room number are required");
      return;
    }
    const validItems = (manualOrderForm.items || []).filter((it) => it.name && it.name.trim() !== "");
    if (validItems.length === 0) {
      toast.error("Add at least one item to the order");
      return;
    }
    const total = calculateManualOrderTotal();

    setManualOrderSaving(true);
    try {
      const payload = {
        guest_name: manualOrderForm.guest_name.trim(),
        room_number: manualOrderForm.room_number.trim(),
        whatsapp: manualOrderForm.whatsapp.trim() || "+910000000000",
        property_id: propertyId,
        payment_mode: manualOrderForm.payment_mode || "Cash",
        notes: manualOrderForm.notes.trim(),
        is_manual: true,
        items: validItems.map((it) => ({
          item_id: it.item_id || `manual_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
          name: it.name.trim(),
          quantity: Number(it.quantity) || 1,
          price: Number(it.price) || 0,
        })),
        total: total,
      };
      await api.post("/orders", payload);
      toast.success(`Paid food order recorded for ${manualOrderForm.guest_name} (₹${total}) ✅`);
      setShowManualOrderDialog(false);
      setManualOrderForm({
        guest_name: "",
        room_number: "",
        whatsapp: "",
        payment_mode: "Cash",
        notes: "",
        items: [{ item_id: "", name: "", quantity: 1, price: 0 }],
      });
      fetchData();
      setActiveTab("orders");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create manual food order");
    } finally {
      setManualOrderSaving(false);
    }
  };

  // ---- Manual Experience Booking Helpers ----
  const handleSubmitManualBooking = async () => {
    if (!manualBookingForm.experience_id) {
      toast.error("Please select an experience");
      return;
    }
    if (!manualBookingForm.guest_name.trim() || !manualBookingForm.room_number.trim()) {
      toast.error("Guest name and room number are required");
      return;
    }

    const selectedExp = experiences.find((e) => e.id === manualBookingForm.experience_id);
    const expTitle = selectedExp?.title || manualBookingForm.experience_title || "Experience";
    const unitPrice = parseFloat(selectedExp?.price || 0);
    const guests = Math.max(1, parseInt(manualBookingForm.guests) || 1);
    const total = unitPrice * guests;

    setManualBookingSaving(true);
    try {
      const payload = {
        experience_id: manualBookingForm.experience_id,
        experience_title: expTitle,
        guest_name: manualBookingForm.guest_name.trim(),
        room_number: manualBookingForm.room_number.trim(),
        whatsapp: manualBookingForm.whatsapp.trim() || "+910000000000",
        property_id: propertyId,
        date_slots: `${manualBookingForm.booking_date} ${manualBookingForm.date_slots || ""}`.trim(),
        guests: guests,
        is_manual: true,
        payment_mode: manualBookingForm.payment_mode || "Cash",
        notes: manualBookingForm.notes.trim(),
        total: total,
      };
      await api.post("/bookings", payload);
      toast.success(`Experience booking confirmed for ${manualBookingForm.guest_name} ✅`);
      setShowManualBookingDialog(false);
      setManualBookingForm({
        experience_id: "",
        experience_title: "",
        guest_name: "",
        room_number: "",
        whatsapp: "",
        booking_date: new Date().toISOString().split("T")[0],
        date_slots: "Morning 6:00 AM",
        guests: 1,
        payment_mode: "Cash",
        notes: "",
      });
      fetchData();
      setActiveTab("bookings");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create experience booking");
    } finally {
      setManualBookingSaving(false);
    }
  };

  // ---- Complimentary Order Helpers ----
  const addCompItem = () => {
    setCompForm((f) => ({ ...f, items: [...f.items, { item_id: "", name: "", quantity: 1 }] }));
  };

  const removeCompItem = (idx) => {
    setCompForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  };

  const updateCompItem = (idx, field, value) => {
    setCompForm((f) => {
      const items = [...f.items];
      items[idx] = { ...items[idx], [field]: value };
      if (field === "item_id") {
        const found = menuItems.find((m) => m.id === value);
        if (found) items[idx].name = found.name;
      }
      return { ...f, items };
    });
  };

  const handleSubmitComplimentary = async () => {
    if (!compForm.guest_name.trim() || !compForm.room_number.trim()) {
      toast.error("Guest name and room number are required");
      return;
    }
    const validItems = compForm.items.filter((it) => it.name && it.name.trim() !== "");
    if (validItems.length === 0) {
      toast.error("Add at least one item");
      return;
    }
    setCompSaving(true);
    try {
      const payload = {
        guest_name: compForm.guest_name.trim(),
        room_number: compForm.room_number.trim(),
        property_id: propertyId,
        meal_plan: compForm.meal_plan,
        booking_reference: compForm.booking_reference.trim(),
        notes: compForm.notes.trim(),
        items: validItems.map((it) => ({
          item_id: it.item_id || `custom_${Date.now()}`,
          name: it.name.trim(),
          quantity: Number(it.quantity) || 1,
          price: 0,
        })),
      };
      await api.post("/orders/complimentary", payload);
      toast.success(`Complimentary meal recorded for ${compForm.guest_name} ✅`);
      setShowComplimentaryDialog(false);
      setCompForm({
        guest_name: "",
        room_number: "",
        meal_plan: "CP",
        booking_reference: "",
        notes: "",
        items: [{ item_id: "", name: "", quantity: 1 }],
      });
      fetchData();
      setActiveTab("orders");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to record complimentary meal");
    } finally {
      setCompSaving(false);
    }
  };

  const canToggleFood =
    user &&
    (user.role === "admin" ||
      user.role === "global" ||
      user.role === "manager" ||
      user.role === "kitchen" ||
      user.can_manage_settings ||
      user.can_edit_menu);

  const canToggleExp =
    user &&
    (user.role === "admin" ||
      user.role === "global" ||
      user.role === "manager" ||
      user.can_manage_settings ||
      user.can_manage_experiences);

  const hasAdminAccess =
    user &&
    (user.role === "admin" ||
      user.role === "global" ||
      user.can_manage_settings ||
      user.can_edit_menu ||
      user.can_manage_experiences ||
      user.can_manage_users ||
      user.can_view_reports ||
      user.can_view_analytics ||
      user.can_manage_discounts ||
      user.can_manage_qrcodes ||
      user.can_view_logs ||
      user.can_edit_messages);

  // ---- RESTAURANT MASTER SWITCH & CATEGORY TOGGLE HELPERS (CALM, EYE-FRIENDLY UI) ----
  const renderRestaurantMasterSwitch = () => {
    const isRestaurantOpen = propertySettings?.food_enabled !== false;
    return (
      <div
        className={`rounded-2xl p-4 sm:p-5 border transition-all shadow-sm ${
          isRestaurantOpen
            ? "bg-white border-emerald-200/90 ring-1 ring-emerald-500/10"
            : "bg-slate-50 border-rose-200/90 ring-1 ring-rose-500/10"
        }`}
        data-testid="restaurant-master-card"
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-start sm:items-center gap-3.5">
            <div
              className={`w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 transition-colors ${
                isRestaurantOpen ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"
              }`}
            >
              <UtensilsCrossed className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="font-bold text-base text-[#264653]">Restaurant Master Switch</h2>
                <span
                  className={`text-xs font-bold px-2.5 py-0.5 rounded-full inline-flex items-center gap-1.5 transition-colors ${
                    isRestaurantOpen
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : "bg-rose-50 text-rose-700 border border-rose-200"
                  }`}
                >
                  <span
                    className={`w-2 h-2 rounded-full ${
                      isRestaurantOpen ? "bg-emerald-500 animate-pulse" : "bg-rose-500"
                    }`}
                  />
                  {isRestaurantOpen ? "RESTAURANT OPEN" : "RESTAURANT CLOSED"}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {isRestaurantOpen
                  ? "Kitchen is accepting orders. Guests can view the menu and place live food orders."
                  : "Kitchen is paused. Guests will see the restaurant as closed and cannot place orders."}
              </p>
            </div>
          </div>

          {canToggleFood && (
            <div className="flex items-center justify-between sm:justify-end gap-3 pt-2 sm:pt-0 border-t sm:border-t-0 border-gray-100">
              <span className="text-xs font-semibold text-gray-600 sm:hidden">
                {isRestaurantOpen ? "Kitchen Accepting Orders" : "Kitchen Paused"}
              </span>
              <div className="flex items-center gap-2.5">
                <span className="text-xs font-medium text-gray-500 hidden sm:inline">
                  {isRestaurantOpen ? "Open" : "Closed"}
                </span>
                <Switch
                  checked={isRestaurantOpen}
                  disabled={togglingFood}
                  onCheckedChange={handleToggleFood}
                  className="data-[state=checked]:bg-[#2A9D8F] scale-110 cursor-pointer"
                  data-testid="toggle-restaurant-master"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCategoryQuickToggles = (showAdminLink = true) => {
    return (
      <div className="bg-white rounded-2xl p-4 sm:p-5 border border-slate-200/80 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3.5">
          <div>
            <h3 className="font-bold text-[#264653] text-sm sm:text-base flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#2A9D8F]" />
              <span>Meal Category Quick Toggles</span>
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Switch specific meal categories ON/OFF instantly without affecting the rest of the menu.
            </p>
          </div>
          {showAdminLink && (user?.role === "admin" || user?.role === "global" || user?.can_edit_menu) && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                navigate(`/admin/menu?property=${propertyId}`);
              }}
              className="text-xs flex items-center gap-1 text-[#2A9D8F] border-[#2A9D8F]/30 h-8 self-start sm:self-auto rounded-xl"
            >
              <span>Full Menu Admin</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-2.5">
          {CATEGORIES_LIST.map((cat) => {
            const setting = categorySettings.find((c) => c.category === cat.key);
            const isOpen = setting ? setting.is_enabled !== false && setting.is_active !== false : true;
            const isToggling = togglingCat[cat.key];

            return (
              <div
                key={cat.key}
                onClick={() => !isToggling && handleToggleCategory(cat.key)}
                className={`p-3 rounded-xl border transition-all cursor-pointer select-none flex flex-col justify-between gap-2.5 ${
                  isOpen
                    ? "bg-white border-slate-200/90 hover:border-emerald-300 shadow-xs"
                    : "bg-slate-50/90 border-slate-200/70 opacity-75 hover:opacity-100"
                }`}
                data-testid={`category-toggle-${cat.key}`}
              >
                <div className="flex items-center justify-between gap-1">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-base flex-shrink-0">{cat.icon}</span>
                    <span className="font-semibold text-xs text-[#264653] truncate">{cat.label}</span>
                  </div>
                  <span
                    className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                      isOpen
                        ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                        : "bg-slate-100 text-slate-500 border border-slate-200"
                    }`}
                  >
                    {isOpen ? "OPEN" : "OFF"}
                  </span>
                </div>

                <div className="flex items-center justify-between pt-1 border-t border-gray-100">
                  <span className="text-[11px] text-gray-500 font-medium">
                    {isOpen ? "Active" : "Paused"}
                  </span>
                  <Switch
                    checked={isOpen}
                    disabled={isToggling}
                    onCheckedChange={() => handleToggleCategory(cat.key)}
                    className="data-[state=checked]:bg-[#2A9D8F]"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ---- TAB 1: RENDER FOOD & KITCHEN ORDERS ----
  const renderOrders = () => {
    let list = Array.isArray(orders) ? applyDateRange(orders) : [];
    if (orderFilter !== "all") {
      list = list.filter((o) => o.status === orderFilter);
    }
    if (orderSearch.trim()) {
      const q = orderSearch.toLowerCase();
      list = list.filter(
        (o) =>
          (o.guest_name || "").toLowerCase().includes(q) ||
          (o.room_number || "").toLowerCase().includes(q) ||
          (o.id || "").toLowerCase().includes(q)
      );
    }

    return (
      <div className="space-y-6">
        {/* Top Control Section: Restaurant Master Switch */}
        {renderRestaurantMasterSwitch()}

        {/* Action Buttons & Filters Header */}
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={() => setShowManualOrderDialog(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs flex items-center gap-1.5 h-9 rounded-xl shadow-sm"
              data-testid="btn-new-food-order"
            >
              <Plus className="w-4 h-4" />
              <span>+ New Food Order</span>
            </Button>
            <Button
              onClick={() => setShowComplimentaryDialog(true)}
              className="bg-purple-600 hover:bg-purple-700 text-white font-semibold text-xs flex items-center gap-1.5 h-9 rounded-xl shadow-sm"
              data-testid="btn-record-comp-meal"
            >
              <Gift className="w-4 h-4" />
              <span>🎁 Record Meal</span>
            </Button>
          </div>

          <div className="relative w-full sm:w-60">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search by guest or room..."
              value={orderSearch}
              onChange={(e) => setOrderSearch(e.target.value)}
              className="pl-8 h-9 text-xs rounded-xl bg-white"
            />
          </div>
        </div>

        {/* Date Range Toggle */}
        <div className="flex gap-2 mb-1">
          {[["today", "Today"], ["7days", "Last 7 Days"]].map(([val, label]) => (
            <button
              key={val}
              onClick={() => setDateRange(val)}
              className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
                dateRange === val ? "bg-amber-500 text-white" : "bg-white text-gray-600 border hover:bg-gray-50"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Status Filter Pills */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {["all", "pending", "paid", "delivered", "complimentary"].map((filterKey) => (
            <button
              key={filterKey}
              onClick={() => setOrderFilter(filterKey)}
              className={`px-3 py-1 rounded-full text-xs font-semibold capitalize whitespace-nowrap transition-all ${
                orderFilter === filterKey ? "bg-[#264653] text-white" : "bg-white text-gray-700 border hover:bg-gray-50"
              }`}
            >
              {filterKey === "all" ? "All Orders" : filterKey} (
              {filterKey === "all" ? applyDateRange(orders).length : applyDateRange(orders).filter((o) => o.status === filterKey).length})
            </button>
          ))}
        </div>

        {/* Orders Cards List */}
        {list.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-[#E8E4D9]">
            <UtensilsCrossed className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500 text-sm font-medium">No food orders match the current filter.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {list.map((order) => (
              <Card
                key={order.id}
                className={`border-l-4 transition-all bg-white ${
                  order.status === "complimentary"
                    ? "border-l-purple-500"
                    : order.status === "paid"
                    ? "border-l-emerald-500"
                    : order.status === "delivered"
                    ? "border-l-teal-600"
                    : "border-l-[#FFC107]"
                }`}
              >
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-bold text-sm text-[#264653]">{order.guest_name}</p>
                        <span className="bg-amber-100 text-amber-900 text-xs font-semibold px-2 py-0.5 rounded-full">
                          Room {order.room_number}
                        </span>
                        {order.is_manual && (
                          <span className="bg-blue-100 text-blue-800 text-[10px] font-bold px-1.5 py-0.2 rounded-full">
                            Manual Order ({order.payment_mode || "Cash"})
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-gray-400 mt-0.5">
                        ID: {order.id?.slice(0, 8).toUpperCase()} • {new Date(order.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        {order.fulfillment_code && (
                          <span className="ml-2 font-bold text-emerald-700">Code: #{order.fulfillment_code}</span>
                        )}
                      </p>
                      {order.status === "complimentary" && order.meal_plan && (
                        <p className="text-xs text-purple-600 font-semibold mt-0.5">📋 {order.meal_plan} Plan</p>
                      )}
                    </div>
                    {getStatusBadge(order.status)}
                  </div>

                  <div className="bg-[#FBF9F6] p-2.5 rounded-xl text-xs space-y-1 mb-2 border border-gray-100">
                    {(order.items || []).map((item, i) => (
                      <div key={i} className="flex justify-between items-center text-gray-700">
                        <span>
                          {item.name} <span className="font-bold">x{item.quantity}</span>
                        </span>
                        <span>{order.status !== "complimentary" ? `₹${(Number(item.price) || 0) * (Number(item.quantity) || 1)}` : "Complimentary"}</span>
                      </div>
                    ))}
                    {order.status !== "complimentary" && (
                      <div className="border-t pt-1 flex justify-between font-bold text-sm text-[#264653]">
                        <span>Total</span>
                        <span className="text-emerald-700">₹{order.total}</span>
                      </div>
                    )}
                  </div>

                  {order.notes && (
                    <p className="text-xs text-gray-600 italic mb-2">Note: {order.notes}</p>
                  )}

                  {/* Order Actions */}
                  <div className="flex gap-2 pt-1 flex-wrap">
                    {order.status === "pending" && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => updateStatus("orders", order.id, "approved")}
                          className="bg-green-600 hover:bg-green-700 text-white h-8 text-xs font-semibold"
                          disabled={actionLoading === order.id}
                        >
                          <CheckCircle className="w-3.5 h-3.5 mr-1" /> Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => updateStatus("orders", order.id, "rejected")}
                          className="h-8 text-xs font-semibold"
                          disabled={actionLoading === order.id}
                        >
                          <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                        </Button>
                      </>
                    )}
                    {(order.status === "paid" || order.status === "approved" || order.status === "complimentary") && (
                      <Button
                        size="sm"
                        onClick={() => updateStatus("orders", order.id, "delivered")}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs font-semibold"
                        disabled={actionLoading === order.id}
                      >
                        <Truck className="w-3.5 h-3.5 mr-1" /> Mark Delivered
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ---- TAB 2: RENDER EXPERIENCES & BOOKINGS ----
  const renderBookings = () => {
    let list = Array.isArray(bookings) ? applyDateRange(bookings) : [];
    if (bookingFilter !== "all") {
      list = list.filter((b) => b.status === bookingFilter);
    }
    if (bookingSearch.trim()) {
      const q = bookingSearch.toLowerCase();
      list = list.filter(
        (b) =>
          (b.guest_name || "").toLowerCase().includes(q) ||
          (b.room_number || "").toLowerCase().includes(q) ||
          (b.experience_title || "").toLowerCase().includes(q)
      );
    }

    return (
      <div className="space-y-6">
        {/* Top Operational Bar */}
        <div className="bg-white rounded-2xl p-4 md:p-5 border border-[#E8E4D9] shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="font-bold text-[#264653] text-base flex items-center gap-2">
              <Compass className="w-4 h-4 text-[#2A9D8F]" />
              <span>Experiences Booking Controls</span>
            </h2>
            <p className="text-xs text-gray-500">Manage tours, walking trails, yoga sessions, and live bookings.</p>
          </div>
          <div className="flex items-center gap-2">
            <span
              className={`text-xs font-bold px-3 py-1 rounded-full ${
                propertySettings?.experiences_enabled !== false ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"
              }`}
            >
              Bookings: {propertySettings?.experiences_enabled !== false ? "ACTIVE 🟢" : "PAUSED 🔴"}
            </span>
            {canToggleExp && (
              <Button
                size="sm"
                disabled={togglingExp}
                onClick={handleToggleExperiences}
                className={`text-xs font-semibold h-8 rounded-xl ${
                  propertySettings?.experiences_enabled !== false
                    ? "bg-red-600 hover:bg-red-700 text-white"
                    : "bg-emerald-600 hover:bg-emerald-700 text-white"
                }`}
                data-testid="toggle-experiences-master"
              >
                {propertySettings?.experiences_enabled !== false ? "Pause Bookings" : "Activate Bookings"}
              </Button>
            )}
          </div>
        </div>

        {/* Action Header & Search */}
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
          <Button
            onClick={() => setShowManualBookingDialog(true)}
            className="bg-[#2A9D8F] hover:bg-[#21867a] text-white font-semibold text-xs flex items-center gap-1.5 h-9 rounded-xl shadow-sm self-start sm:self-auto"
            data-testid="btn-new-experience-booking"
          >
            <Plus className="w-4 h-4" />
            <span>+ New Experience Booking</span>
          </Button>

          <div className="relative w-full sm:w-60">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search bookings..."
              value={bookingSearch}
              onChange={(e) => setBookingSearch(e.target.value)}
              className="pl-8 h-9 text-xs rounded-xl bg-white"
            />
          </div>
        </div>

        {/* Date Range Toggle */}
        <div className="flex gap-2 mb-1">
          {[["today", "Today"], ["7days", "Last 7 Days"]].map(([val, label]) => (
            <button key={val} onClick={() => setDateRange(val)}
              className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${dateRange === val ? "bg-amber-500 text-white" : "bg-white text-gray-600 border hover:bg-gray-50"}`}>
              {label}
            </button>
          ))}
        </div>

        {/* Booking Filter Pills */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {["all", "pending", "confirmed", "vendor_unresponsive", "cancelled"].map((filterKey) => (
            <button
              key={filterKey}
              onClick={() => setBookingFilter(filterKey)}
              className={`px-3 py-1 rounded-full text-xs font-semibold capitalize whitespace-nowrap transition-all ${
                bookingFilter === filterKey ? "bg-[#264653] text-white" : "bg-white text-gray-700 border hover:bg-gray-50"
              }`}
            >
              {filterKey === "all" ? "All Bookings" : filterKey.replace("_", " ")} (
              {filterKey === "all" ? applyDateRange(bookings).length : applyDateRange(bookings).filter((b) => b.status === filterKey).length})
            </button>
          ))}
        </div>

        {/* Bookings Cards List */}
        {list.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-[#E8E4D9]">
            <Compass className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500 text-sm font-medium">No experience bookings match the current filter.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {list.map((booking) => (
              <Card key={booking.id} className="border-l-4 border-l-[#2A9D8F] bg-white shadow-sm">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-bold text-sm text-[#264653]">{booking.experience_title}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-gray-700 font-semibold">{booking.guest_name}</span>
                        <span className="bg-amber-100 text-amber-900 text-xs font-semibold px-2 py-0.5 rounded-full">
                          Room {booking.room_number}
                        </span>
                        {booking.is_manual && (
                          <span className="bg-teal-100 text-teal-800 text-[10px] font-bold px-1.5 py-0.2 rounded-full">
                            Manual Booking ({booking.payment_mode || "Cash"})
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        📅 Slot: <strong>{booking.date_slots || "Not specified"}</strong> • {booking.guests || 1} guest(s)
                      </p>
                      {booking.total && (
                        <p className="text-xs font-bold text-emerald-700 mt-0.5">Total: ₹{booking.total}</p>
                      )}
                    </div>
                    {getStatusBadge(booking.status)}
                  </div>

                  {booking.notes && (
                    <p className="text-xs text-gray-600 italic mb-2">Note: {booking.notes}</p>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2 pt-1 flex-wrap">
                    {booking.status === "pending" && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => updateStatus("bookings", booking.id, "approved")}
                          className="bg-green-600 hover:bg-green-700 text-white h-8 text-xs font-semibold"
                          disabled={actionLoading === booking.id}
                        >
                          <CheckCircle className="w-3.5 h-3.5 mr-1" /> Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => updateStatus("bookings", booking.id, "rejected")}
                          className="h-8 text-xs font-semibold"
                          disabled={actionLoading === booking.id}
                        >
                          <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                        </Button>
                      </>
                    )}
                    {booking.status === "vendor_unresponsive" && (
                      <Button
                        size="sm"
                        onClick={() => updateStatus("bookings", booking.id, "confirmed")}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs font-semibold"
                        disabled={actionLoading === booking.id}
                      >
                        <Check className="w-3.5 h-3.5 mr-1" /> Confirm Directly
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ---- TAB 3: RENDER GUEST SERVICES & REQUESTS ----
  const renderRequests = () => {
    let list = Array.isArray(requests) ? applyDateRange(requests) : [];
    if (requestFilter !== "all") {
      list = list.filter((r) => r.status === requestFilter);
    }

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-2xl p-4 md:p-5 border border-[#E8E4D9] shadow-sm">
          <h2 className="font-bold text-[#264653] text-base flex items-center gap-2">
            <Bell className="w-4 h-4 text-[#FFC107]" />
            <span>Guest Service & Housekeeping Requests</span>
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">Manage room cleaning, extra towels, water bottles, and front desk assistance.</p>
        </div>

        {/* Date Range Toggle */}
        <div className="flex gap-2 mb-1">
          {[["today", "Today"], ["7days", "Last 7 Days"]].map(([val, label]) => (
            <button key={val} onClick={() => setDateRange(val)}
              className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${dateRange === val ? "bg-amber-500 text-white" : "bg-white text-gray-600 border hover:bg-gray-50"}`}>
              {label}
            </button>
          ))}
        </div>

        {/* Filter Pills */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {["all", "pending", "approved", "in_progress", "resolved", "completed"].map((filterKey) => (
            <button
              key={filterKey}
              onClick={() => setRequestFilter(filterKey)}
              className={`px-3 py-1 rounded-full text-xs font-semibold capitalize whitespace-nowrap transition-all ${
                requestFilter === filterKey ? "bg-[#264653] text-white" : "bg-white text-gray-700 border hover:bg-gray-50"
              }`}
            >
              {filterKey === "all" ? "All Requests" : filterKey.replace("_", " ")} (
              {filterKey === "all" ? applyDateRange(requests).length : applyDateRange(requests).filter((r) => r.status === filterKey).length})
            </button>
          ))}
        </div>

        {/* Requests Cards List */}
        {list.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-[#E8E4D9]">
            <Bell className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500 text-sm font-medium">No service requests match the current filter.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {list.map((req) => (
              <Card key={req.id} className="border-l-4 border-l-[#FFC107] bg-white shadow-sm">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-bold text-sm text-[#264653] capitalize">{req.category || "Service Request"}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-gray-700 font-semibold">{req.guest_name}</span>
                        <span className="bg-amber-100 text-amber-900 text-xs font-semibold px-2 py-0.5 rounded-full">
                          Room {req.room_number}
                        </span>
                      </div>
                    </div>
                    {getStatusBadge(req.status)}
                  </div>

                  <p className="text-xs text-gray-700 bg-gray-50 p-2.5 rounded-xl border border-gray-100 mt-2 mb-3">
                    {req.message}
                  </p>

                  <div className="flex gap-2 pt-1 flex-wrap">
                    {req.status === "pending" && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => updateStatus("requests", req.id, "approved")}
                          className="bg-green-600 hover:bg-green-700 text-white h-8 text-xs font-semibold"
                          disabled={actionLoading === req.id}
                        >
                          <CheckCircle className="w-3.5 h-3.5 mr-1" /> Acknowledge
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => updateStatus("requests", req.id, "rejected")}
                          className="h-8 text-xs font-semibold"
                          disabled={actionLoading === req.id}
                        >
                          <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                        </Button>
                      </>
                    )}
                    {req.status === "approved" && (
                      <Button
                        size="sm"
                        onClick={() => updateStatus("requests", req.id, "resolved")}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs font-semibold"
                        disabled={actionLoading === req.id}
                      >
                        <Check className="w-3.5 h-3.5 mr-1" /> Mark Resolved
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ---- TAB 4: RENDER MENU & ITEM AVAILABILITY ----
  const renderMenu = () => {
    const filteredItems = menuItems.filter((item) => {
      if (menuFilterCat !== "all" && item.category !== menuFilterCat) return false;
      if (menuSearch.trim()) {
        const q = menuSearch.toLowerCase();
        return (item.name || "").toLowerCase().includes(q) || (item.category || "").toLowerCase().includes(q);
      }
      return true;
    });

    return (
      <div className="space-y-6">
        {/* 1. Dedicated Single Restaurant Master Switch at top of Menu */}
        {renderRestaurantMasterSwitch()}

        {/* 2. Meal Category Quick Toggles */}
        {renderCategoryQuickToggles(true)}

        {/* 3. Individual Dish Stock Availability with Calm, Eye-Friendly Switch Controls */}
        <div className="bg-white rounded-2xl p-4 sm:p-5 border border-slate-200/80 shadow-sm">
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between mb-4">
            <div>
              <h3 className="font-bold text-[#264653] text-sm sm:text-base">Individual Dish Stock Availability</h3>
              <p className="text-xs text-gray-500 mt-0.5">Toggle dishes In Stock or Out of Stock when ingredients run out.</p>
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search dishes..."
                value={menuSearch}
                onChange={(e) => setMenuSearch(e.target.value)}
                className="pl-8 h-9 text-xs rounded-xl bg-slate-50/60 border-slate-200"
              />
            </div>
          </div>

          {/* Filter Pills */}
          <div className="flex gap-1.5 overflow-x-auto pb-2 mb-3 scrollbar-none">
            <button
              onClick={() => setMenuFilterCat("all")}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition-all whitespace-nowrap ${
                menuFilterCat === "all" ? "bg-[#264653] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              All ({menuItems.length})
            </button>
            {CATEGORIES_LIST.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setMenuFilterCat(cat.key)}
                className={`px-3 py-1 rounded-full text-xs font-semibold capitalize whitespace-nowrap transition-all ${
                  menuFilterCat === cat.key ? "bg-[#264653] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                {cat.label} ({menuItems.filter((i) => i.category === cat.key).length})
              </button>
            ))}
          </div>

          {/* Items List */}
          {filteredItems.length === 0 ? (
            <p className="text-center text-gray-400 py-8 text-xs">No menu items found</p>
          ) : (
            <div className="divide-y divide-slate-100 max-h-[550px] overflow-y-auto px-1">
              {filteredItems.map((item) => {
                const isAvail = item.is_available !== false;
                const catSetting = categorySettings.find((c) => c.category === item.category);
                const isCatClosed = catSetting && (catSetting.is_enabled === false || catSetting.is_active === false);
                const isToggling = togglingItem[item.id];

                return (
                  <div
                    key={item.id}
                    className="py-3 px-2 flex items-center justify-between gap-3 hover:bg-slate-50/70 rounded-xl transition-colors"
                  >
                    <div className="flex-1 min-w-0 pr-2">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-semibold text-xs sm:text-sm text-[#264653] truncate">
                          {item.name}
                        </span>
                        {item.is_vegetarian ? (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.2 rounded text-[10px] font-semibold bg-green-50 text-green-700 border border-green-200 flex-shrink-0">
                            <Leaf className="w-2.5 h-2.5 text-green-600" /> Veg
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-1.5 py-0.2 rounded text-[10px] font-semibold bg-amber-50 text-amber-800 border border-amber-200 flex-shrink-0">
                            Non-Veg
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-[11px] text-gray-500 mt-0.5 flex-wrap">
                        <span className="capitalize font-medium text-gray-600">{item.category}</span>
                        <span>•</span>
                        <span className="font-bold text-[#264653]">₹{item.price}</span>
                        {isCatClosed && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.2 rounded text-[10px] font-medium bg-rose-50 text-rose-600 border border-rose-200">
                            Category Paused
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2.5 flex-shrink-0">
                      <span
                        className={`text-[11px] font-semibold px-2 py-0.5 rounded-full transition-colors hidden sm:inline-block ${
                          isAvail
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                            : "bg-slate-100 text-slate-500 border border-slate-200"
                        }`}
                      >
                        {isAvail ? "In Stock" : "Out of Stock"}
                      </span>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={isAvail}
                          disabled={isToggling}
                          onCheckedChange={() => handleToggleItemAvailability(item.id)}
                          className="data-[state=checked]:bg-[#2A9D8F]"
                          data-testid={`toggle-avail-${item.id}`}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#F9F7F2]">
      {/* Impersonation Banner */}
      {impersonator && (
        <div className="bg-amber-500 text-black px-4 py-2 text-xs font-bold flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-2">
            <span>🛡️ Logged in as <strong>{user.username}</strong> ({user.role})</span>
          </div>
          <Button
            size="sm"
            onClick={handleReturnToAdmin}
            className="bg-white text-black hover:bg-gray-100 text-xs font-bold h-7 px-2.5 rounded shadow-sm"
          >
            Exit Impersonation
          </Button>
        </div>
      )}

      {/* Main Header */}
      <header className="bg-[#264653] text-white p-3 sm:p-4 sticky top-0 z-20 shadow-md">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 sm:gap-3">
          <div className="flex items-center justify-between w-full sm:w-auto">
            <div>
              <h1 className="text-base sm:text-lg font-bold tracking-tight">Operations Dashboard</h1>
              <p className="text-xs text-white/80">
                {user.username} <span className="capitalize font-semibold text-[#E9C46A]">({user.role})</span>
                {propertySettings?.property_name && (
                  <span className="text-white/60"> · {propertySettings.property_name}</span>
                )}
              </p>
            </div>

            <div className="flex items-center gap-1 sm:hidden">
              <Button size="sm" variant="ghost" onClick={fetchData} className="text-white hover:bg-white/10 h-8 w-8 p-0" title="Refresh Data">
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={handleLogout} className="text-white hover:bg-white/10 h-8 w-8 p-0" title="Logout">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto justify-between sm:justify-end">
            {/* Property Selector for Admins & Managers */}
            {hasAdminAccess && properties.length > 1 && (
              <Select value={propertyId} onValueChange={handlePropertyChange}>
                <SelectTrigger className="h-8 text-xs bg-white/10 border-white/20 text-white flex-1 sm:w-40 rounded-lg">
                  <Building2 className="w-3.5 h-3.5 mr-1 text-[#E9C46A]" />
                  <SelectValue placeholder="Property" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((p) => (
                    <SelectItem key={p.property_id} value={p.property_id}>
                      {p.property_name || p.property_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Broadcast button — visible to managers */}
            {user?.role === "manager" && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate(`/admin/broadcast`)}
                className="h-8 text-xs bg-purple-500/20 text-white border-purple-300/30 hover:bg-purple-500/30 rounded-lg"
                data-testid="btn-broadcast"
              >
                <Bell className="w-3.5 h-3.5 mr-1 text-purple-300" />
                <span>Broadcast</span>
              </Button>
            )}

            {hasAdminAccess && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  navigate(`/admin/dashboard?property=${propertyId}`);
                }}
                className="h-8 text-xs bg-white/10 text-white border-white/20 hover:bg-white/20 rounded-lg"
                data-testid="btn-admin-portal"
              >
                <Shield className="w-3.5 h-3.5 mr-1 text-[#E9C46A]" />
                <span>Admin Portal</span>
              </Button>
            )}

            <div className="hidden sm:flex items-center gap-1">
              <Button size="sm" variant="ghost" onClick={fetchData} className="text-white hover:bg-white/10 h-8 w-8 p-0" title="Refresh Data">
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={handleLogout} className="text-white hover:bg-white/10 h-8 w-8 p-0" title="Logout">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs Navigation */}
      <div className="bg-white border-b border-[#E8E4D9] sticky top-[62px] sm:top-[64px] z-10 shadow-xs">
        <div className="max-w-5xl mx-auto px-3 sm:px-4 flex gap-1.5 sm:gap-2.5 overflow-x-auto py-2 scrollbar-none">
          <button
            onClick={() => setActiveTab("orders")}
            className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-3.5 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all whitespace-nowrap flex-shrink-0 ${
              activeTab === "orders" ? "bg-[#FFC107] text-[#264653] shadow-xs" : "text-gray-600 hover:bg-gray-100"
            }`}
            data-testid="tab-orders"
          >
            <span>🍽️ Food & Kitchen</span>
            <span className="bg-black/10 px-1.5 py-0.2 rounded-full text-xs font-black">
              {orders.filter((o) => o.status === "pending" || o.status === "paid").length}
            </span>
          </button>

          <button
            onClick={() => setActiveTab("bookings")}
            className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-3.5 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all whitespace-nowrap flex-shrink-0 ${
              activeTab === "bookings" ? "bg-[#2A9D8F] text-white shadow-xs" : "text-gray-600 hover:bg-gray-100"
            }`}
            data-testid="tab-bookings"
          >
            <span>🎯 Experiences</span>
            <span className="bg-white/20 px-1.5 py-0.2 rounded-full text-xs font-black">
              {bookings.filter((b) => b.status === "pending" || b.status === "vendor_unresponsive").length}
            </span>
          </button>

          <button
            onClick={() => setActiveTab("requests")}
            className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-3.5 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all whitespace-nowrap flex-shrink-0 ${
              activeTab === "requests" ? "bg-[#E76F51] text-white shadow-xs" : "text-gray-600 hover:bg-gray-100"
            }`}
            data-testid="tab-requests"
          >
            <span>🛎️ Guest Services</span>
            <span className="bg-white/20 px-1.5 py-0.2 rounded-full text-xs font-black">
              {requests.filter((r) => r.status === "pending" || r.status === "in_progress").length}
            </span>
          </button>

          <button
            onClick={() => {
              setActiveTab("menu");
              fetchMenuItems();
            }}
            className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-3.5 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all whitespace-nowrap flex-shrink-0 ${
              activeTab === "menu" ? "bg-[#264653] text-white shadow-xs" : "text-gray-600 hover:bg-gray-100"
            }`}
            data-testid="tab-menu"
          >
            <span>📋 Menu & Availability</span>
            <span className="bg-white/20 px-1.5 py-0.2 rounded-full text-xs font-black">{menuItems.length}</span>
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-5xl mx-auto p-4 md:p-6">
        {loading ? (
          <div className="text-center py-16 text-gray-500">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2 text-[#2A9D8F]" />
            <p className="text-sm font-semibold">Loading operations data...</p>
          </div>
        ) : error ? (
          <div className="text-center py-12 px-4 rounded-2xl border border-red-100 bg-red-50/50">
            <h3 className="text-red-800 font-bold mb-2">Connection Error</h3>
            <p className="text-red-600 text-sm mb-4">{error}</p>
            <Button onClick={fetchData} variant="outline" className="border-red-200 text-red-700 hover:bg-red-50">
              Retry Connection
            </Button>
          </div>
        ) : (
          <>
            {activeTab === "orders" && renderOrders()}
            {activeTab === "bookings" && renderBookings()}
            {activeTab === "requests" && renderRequests()}
            {activeTab === "menu" && renderMenu()}
          </>
        )}
      </div>

      {/* ========================================================= */}
      {/* MODAL 1: MANUAL PAID FOOD ORDER DIALOG                     */}
      {/* ========================================================= */}
      <Dialog open={showManualOrderDialog} onOpenChange={setShowManualOrderDialog}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto bg-white rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700 font-bold text-lg">
              <Plus className="w-5 h-5 text-emerald-600" />
              <span>Create Manual Food Order (Paid)</span>
            </DialogTitle>
            <p className="text-xs text-gray-500">
              Record a counter or phone order paid via Cash, UPI, or Front Desk billing.
            </p>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Guest Name *</Label>
                <Input
                  className="mt-1 h-9 text-sm rounded-xl"
                  placeholder="e.g. Rahul Verma"
                  value={manualOrderForm.guest_name}
                  onChange={(e) => setManualOrderForm({ ...manualOrderForm, guest_name: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-xs font-semibold">Room / Bed *</Label>
                <Input
                  className="mt-1 h-9 text-sm rounded-xl"
                  placeholder="e.g. 204"
                  value={manualOrderForm.room_number}
                  onChange={(e) => setManualOrderForm({ ...manualOrderForm, room_number: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Guest WhatsApp (Optional)</Label>
                <Input
                  className="mt-1 h-9 text-sm rounded-xl"
                  placeholder="e.g. +919876543210"
                  value={manualOrderForm.whatsapp}
                  onChange={(e) => setManualOrderForm({ ...manualOrderForm, whatsapp: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-xs font-semibold">Payment Method *</Label>
                <Select
                  value={manualOrderForm.payment_mode}
                  onValueChange={(v) => setManualOrderForm({ ...manualOrderForm, payment_mode: v })}
                >
                  <SelectTrigger className="mt-1 h-9 text-sm rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_MODES.map((pm) => (
                      <SelectItem key={pm} value={pm}>
                        {pm}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Items Picker */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-bold text-gray-700">Order Items *</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={addManualOrderItem}
                  className="text-emerald-700 font-bold h-7 px-2 text-xs hover:bg-emerald-50"
                >
                  <PlusCircle className="w-3.5 h-3.5 mr-1" /> Add Another Item
                </Button>
              </div>

              <div className="space-y-2.5 max-h-56 overflow-y-auto pr-1">
                {manualOrderForm.items.map((item, idx) => (
                  <div key={idx} className="flex gap-2 items-center bg-gray-50 p-2 rounded-xl border border-gray-100">
                    <div className="flex-1">
                      <Select
                        value={item.item_id || "__custom__"}
                        onValueChange={(v) => {
                          if (v === "__custom__") {
                            updateManualOrderItem(idx, "item_id", "");
                            updateManualOrderItem(idx, "name", "");
                            updateManualOrderItem(idx, "price", 0);
                          } else {
                            updateManualOrderItem(idx, "item_id", v);
                          }
                        }}
                      >
                        <SelectTrigger className="h-8 text-xs bg-white rounded-lg">
                          <SelectValue placeholder="Choose from menu..." />
                        </SelectTrigger>
                        <SelectContent className="max-h-56">
                          <SelectItem value="__custom__">✏️ Custom Item (Free text)</SelectItem>
                          {(menuItems || [])
                            .filter((m) => m && m.is_available !== false)
                            .map((m) => (
                              <SelectItem key={m.id} value={m.id}>
                                {m.name} (₹{m.price})
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      {(!item.item_id || !(menuItems || []).find((m) => m && m.id === item.item_id)) && (
                        <div className="flex gap-2 mt-1.5">
                          <Input
                            className="h-7 text-xs bg-white rounded-lg flex-1"
                            placeholder="Dish name"
                            value={item.name}
                            onChange={(e) => updateManualOrderItem(idx, "name", e.target.value)}
                          />
                          <Input
                            className="h-7 text-xs bg-white rounded-lg w-20 text-right"
                            type="number"
                            placeholder="₹ Price"
                            value={item.price || ""}
                            onChange={(e) => updateManualOrderItem(idx, "price", parseFloat(e.target.value) || 0)}
                          />
                        </div>
                      )}
                    </div>

                    <div className="w-16 flex-shrink-0">
                      <Input
                        className="h-8 text-xs text-center bg-white rounded-lg"
                        type="number"
                        min={1}
                        max={50}
                        value={item.quantity}
                        onChange={(e) => updateManualOrderItem(idx, "quantity", parseInt(e.target.value) || 1)}
                        title="Quantity"
                      />
                    </div>

                    <div className="w-16 text-right text-xs font-bold text-[#264653] flex-shrink-0">
                      ₹{(Number(item.price) || 0) * (Number(item.quantity) || 1)}
                    </div>

                    {manualOrderForm.items.length > 1 && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => removeManualOrderItem(idx)}
                        className="text-red-400 hover:text-red-600 h-7 w-7 p-0 flex-shrink-0"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              {/* Live Total Display */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex justify-between items-center mt-3">
                <span className="text-xs font-bold text-emerald-900">Total Calculated:</span>
                <span className="text-base font-black text-emerald-700">₹{calculateManualOrderTotal()}</span>
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold">Special Instructions (Optional)</Label>
              <Input
                className="mt-1 h-9 text-sm rounded-xl"
                placeholder="e.g. Extra spicy, serve hot, pack separately"
                value={manualOrderForm.notes}
                onChange={(e) => setManualOrderForm({ ...manualOrderForm, notes: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter className="mt-4 gap-2">
            <Button
              variant="outline"
              onClick={() => setShowManualOrderDialog(false)}
              disabled={manualOrderSaving}
              className="rounded-xl text-sm"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitManualOrder}
              disabled={manualOrderSaving}
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-bold"
              data-testid="submit-manual-order-btn"
            >
              {manualOrderSaving ? "Placing Order..." : `Place Paid Order (₹${calculateManualOrderTotal()}) ✓`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========================================================= */}
      {/* MODAL 2: MANUAL EXPERIENCE BOOKING DIALOG                  */}
      {/* ========================================================= */}
      <Dialog open={showManualBookingDialog} onOpenChange={setShowManualBookingDialog}>
        <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto bg-white rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#2A9D8F] font-bold text-lg">
              <Compass className="w-5 h-5 text-[#2A9D8F]" />
              <span>Create Experience Booking</span>
            </DialogTitle>
            <p className="text-xs text-gray-500">
              Directly confirm an experience reservation for a walk-in or front desk guest.
            </p>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-semibold">Select Experience *</Label>
              <Select
                value={manualBookingForm.experience_id}
                onValueChange={(v) => {
                  const found = experiences.find((e) => e.id === v);
                  setManualBookingForm({
                    ...manualBookingForm,
                    experience_id: v,
                    experience_title: found?.title || "",
                  });
                }}
              >
                <SelectTrigger className="mt-1 h-9 text-sm rounded-xl">
                  <SelectValue placeholder="Choose experience..." />
                </SelectTrigger>
                <SelectContent className="max-h-56">
                  {experiences.map((exp) => (
                    <SelectItem key={exp.id} value={exp.id}>
                      {exp.title} (₹{exp.price} / person)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Guest Name *</Label>
                <Input
                  className="mt-1 h-9 text-sm rounded-xl"
                  placeholder="e.g. Sarah Jenkins"
                  value={manualBookingForm.guest_name}
                  onChange={(e) => setManualBookingForm({ ...manualBookingForm, guest_name: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-xs font-semibold">Room / Bed *</Label>
                <Input
                  className="mt-1 h-9 text-sm rounded-xl"
                  placeholder="e.g. 102"
                  value={manualBookingForm.room_number}
                  onChange={(e) => setManualBookingForm({ ...manualBookingForm, room_number: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Date *</Label>
                <Input
                  type="date"
                  className="mt-1 h-9 text-sm rounded-xl"
                  value={manualBookingForm.booking_date}
                  onChange={(e) => setManualBookingForm({ ...manualBookingForm, booking_date: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-xs font-semibold">Time Slot / Timing</Label>
                <Input
                  className="mt-1 h-9 text-sm rounded-xl"
                  placeholder="e.g. Morning 6:00 AM"
                  value={manualBookingForm.date_slots}
                  onChange={(e) => setManualBookingForm({ ...manualBookingForm, date_slots: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Number of Guests *</Label>
                <Input
                  type="number"
                  min={1}
                  max={20}
                  className="mt-1 h-9 text-sm rounded-xl"
                  value={manualBookingForm.guests}
                  onChange={(e) => setManualBookingForm({ ...manualBookingForm, guests: parseInt(e.target.value) || 1 })}
                />
              </div>
              <div>
                <Label className="text-xs font-semibold">Payment Mode *</Label>
                <Select
                  value={manualBookingForm.payment_mode}
                  onValueChange={(v) => setManualBookingForm({ ...manualBookingForm, payment_mode: v })}
                >
                  <SelectTrigger className="mt-1 h-9 text-sm rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_MODES.map((pm) => (
                      <SelectItem key={pm} value={pm}>
                        {pm}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold">Guest Phone / WhatsApp (Optional)</Label>
              <Input
                className="mt-1 h-9 text-sm rounded-xl"
                placeholder="e.g. +919876543210"
                value={manualBookingForm.whatsapp}
                onChange={(e) => setManualBookingForm({ ...manualBookingForm, whatsapp: e.target.value })}
              />
            </div>

            {/* Live Pricing Summary */}
            {manualBookingForm.experience_id && (
              <div className="bg-teal-50 border border-teal-200 rounded-xl p-3 flex justify-between items-center">
                <span className="text-xs font-bold text-teal-900">Total Booking Price:</span>
                <span className="text-base font-black text-teal-700">
                  ₹{parseFloat(experiences.find((e) => e.id === manualBookingForm.experience_id)?.price || 0) *
                    Math.max(1, parseInt(manualBookingForm.guests) || 1)}
                </span>
              </div>
            )}

            <div>
              <Label className="text-xs font-semibold">Additional Notes (Optional)</Label>
              <Input
                className="mt-1 h-9 text-sm rounded-xl"
                placeholder="Special pickup point, diet preference, etc."
                value={manualBookingForm.notes}
                onChange={(e) => setManualBookingForm({ ...manualBookingForm, notes: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter className="mt-4 gap-2">
            <Button
              variant="outline"
              onClick={() => setShowManualBookingDialog(false)}
              disabled={manualBookingSaving}
              className="rounded-xl text-sm"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitManualBooking}
              disabled={manualBookingSaving}
              className="bg-[#2A9D8F] hover:bg-[#21867a] text-white rounded-xl text-sm font-bold"
              data-testid="submit-manual-booking-btn"
            >
              {manualBookingSaving ? "Confirming..." : "Confirm Booking ✓"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========================================================= */}
      {/* MODAL 3: COMPLIMENTARY ORDER DIALOG (EP/CP/MAP)             */}
      {/* ========================================================= */}
      <Dialog open={showComplimentaryDialog} onOpenChange={setShowComplimentaryDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto bg-white rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-700 font-bold text-lg">
              <Gift className="w-5 h-5 text-purple-600" />
              <span>Record Complimentary Meal</span>
            </DialogTitle>
            <p className="text-xs text-gray-500">
              For EP / CP / MAP plans or complimentary items. No payment charged (₹0).
            </p>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Guest Name *</Label>
                <Input
                  className="mt-1 h-9 text-sm rounded-xl"
                  placeholder="e.g. Priya Sharma"
                  value={compForm.guest_name}
                  onChange={(e) => setCompForm({ ...compForm, guest_name: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-xs font-semibold">Room / Bed *</Label>
                <Input
                  className="mt-1 h-9 text-sm rounded-xl"
                  placeholder="e.g. 204"
                  value={compForm.room_number}
                  onChange={(e) => setCompForm({ ...compForm, room_number: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold">Meal Plan</Label>
              <Select value={compForm.meal_plan} onValueChange={(v) => setCompForm({ ...compForm, meal_plan: v })}>
                <SelectTrigger className="mt-1 h-9 text-sm rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MEAL_PLANS.map((p) => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs font-semibold">Stay / Booking Reference (Optional)</Label>
              <Input
                className="mt-1 h-9 text-sm rounded-xl"
                placeholder="e.g. HM-2024-1234"
                value={compForm.booking_reference}
                onChange={(e) => setCompForm({ ...compForm, booking_reference: e.target.value })}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-bold text-gray-700">Items Served *</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={addCompItem}
                  className="text-purple-600 font-bold h-6 px-2 text-xs hover:bg-purple-50"
                >
                  <PlusCircle className="w-3.5 h-3.5 mr-1" /> Add Item
                </Button>
              </div>
              <div className="space-y-2">
                {compForm.items.map((item, idx) => (
                  <div key={idx} className="flex gap-2 items-start">
                    <div className="flex-1">
                      {menuItems.length > 0 ? (
                        <Select
                          value={item.item_id || "__custom__"}
                          onValueChange={(v) => {
                            if (v === "__custom__") {
                              updateCompItem(idx, "item_id", "");
                              updateCompItem(idx, "name", "");
                            } else {
                              updateCompItem(idx, "item_id", v);
                            }
                          }}
                        >
                          <SelectTrigger className="h-8 text-xs bg-white rounded-lg">
                            <SelectValue placeholder="Select or type item..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__custom__">✏️ Custom / free text</SelectItem>
                            {(menuItems || [])
                              .filter((m) => m && m.is_available !== false)
                              .map((m) => (
                                <SelectItem key={m.id} value={m.id}>
                                  {m.name}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      ) : null}
                      {(!item.item_id || !(menuItems || []).find((m) => m && m.id === item.item_id)) && (
                        <Input
                          className="mt-1 h-8 text-xs bg-white rounded-lg"
                          placeholder="Item name"
                          value={item.name}
                          onChange={(e) => updateCompItem(idx, "name", e.target.value)}
                        />
                      )}
                    </div>
                    <div className="w-16">
                      <Input
                        className="h-8 text-xs text-center bg-white rounded-lg"
                        type="number"
                        min={1}
                        max={50}
                        value={item.quantity}
                        onChange={(e) => updateCompItem(idx, "quantity", parseInt(e.target.value) || 1)}
                        title="Quantity"
                      />
                    </div>
                    {compForm.items.length > 1 && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => removeCompItem(idx)}
                        className="text-red-400 hover:text-red-600 h-8 w-8 p-0"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold">Additional Notes (Optional)</Label>
              <Input
                className="mt-1 h-9 text-sm rounded-xl"
                placeholder="Any extra info"
                value={compForm.notes}
                onChange={(e) => setCompForm({ ...compForm, notes: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter className="mt-4 gap-2">
            <Button
              variant="outline"
              onClick={() => setShowComplimentaryDialog(false)}
              disabled={compSaving}
              className="rounded-xl text-sm"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitComplimentary}
              disabled={compSaving}
              className="bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-sm font-bold"
            >
              {compSaving ? "Recording..." : "Record Meal ✓"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
